import type {FilterConfiguration, FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";

export type JourneyScopeFilterConfig = {
    statusColumnId: Uuid;
    activeStatusIds: Array<Uuid>;
    inactiveStatusIds: Array<Uuid>;
};

export type JourneyScopeSelection = "all" | "active" | "inactive";

function uuidArraysMatchAsSets(a: Array<Uuid>, b: Array<string>): boolean {
    if (a.length !== b.length) {
        return false;
    }
    const setB = new Set(b);
    for (const id of a) {
        if (setB.has(String(id)) !== true) {
            return false;
        }
    }
    return true;
}

function removeJourneyScopeInFilterForColumn(
    filterConfiguration: FilterConfiguration,
    statusColumnId: Uuid,
): Array<FilterConfigurationLineItem> {
    return filterConfiguration.lineItems.filter((lineItem) => {
        if (lineItem.columnId !== statusColumnId) {
            return true;
        }
        if (lineItem.operation === FilterOperation.in) {
            return false;
        }
        return true;
    });
}

export function getJourneyScopeSelectionFromFilter(
    filterConfiguration: FilterConfiguration,
    config: JourneyScopeFilterConfig,
): JourneyScopeSelection {
    const lineItem = filterConfiguration.lineItems.find(
        (item): item is FilterConfigurationLineItem & {operation: FilterOperation.in; values: Array<string>} =>
            item.columnId === config.statusColumnId && item.operation === FilterOperation.in,
    );
    if (lineItem === undefined) {
        return "all";
    }
    const values = lineItem.values;
    if (uuidArraysMatchAsSets(config.activeStatusIds, values) === true) {
        return "active";
    }
    if (uuidArraysMatchAsSets(config.inactiveStatusIds, values) === true) {
        return "inactive";
    }
    return "all";
}

/** True when the line item is Display-by Active/Inactive (not a sidebar status slice). */
export function isJourneyScopeManagedFilterLineItem(
    lineItem: FilterConfigurationLineItem,
    config: JourneyScopeFilterConfig,
): boolean {
    if (lineItem.columnId !== config.statusColumnId || lineItem.operation !== FilterOperation.in) {
        return false;
    }
    if ("values" in lineItem === false || Array.isArray(lineItem.values) === false) {
        return false;
    }
    if (uuidArraysMatchAsSets(config.activeStatusIds, lineItem.values) === true) {
        return true;
    }
    if (uuidArraysMatchAsSets(config.inactiveStatusIds, lineItem.values) === true) {
        return true;
    }
    return false;
}

export function applyJourneyScopeSelectionToFilterConfiguration(
    filterConfiguration: FilterConfiguration,
    config: JourneyScopeFilterConfig,
    selection: JourneyScopeSelection,
): FilterConfiguration {
    const baseLineItems = removeJourneyScopeInFilterForColumn(filterConfiguration, config.statusColumnId);

    if (selection === "all") {
        return {
            revisionId: generateUuid(),
            lineItems: baseLineItems,
        };
    }

    const values =
        selection === "active"
            ? config.activeStatusIds.map((id) => String(id))
            : config.inactiveStatusIds.map((id) => String(id));

    const newLineItem: FilterConfigurationLineItem = {
        columnId: config.statusColumnId,
        operation: FilterOperation.in,
        values: values,
    };

    return {
        revisionId: generateUuid(),
        lineItems: [...baseLineItems, newLineItem],
    };
}
